// Pure money math for closing a check. Lives outside CloseCheckDialog.vue so it
// can be unit-tested without mounting the component (four Pinia stores, PrimeVue
// and i18n) — see tests/checks/tender.calculations.test.ts.

/** Only the fields the math needs; PaymentMethod satisfies this structurally. */
export interface SurchargeMethod {
  is_cash: boolean
  surcharge_percentage: number
}

export interface TenderAmounts {
  amount_applied: number
  surcharge: number
}

/**
 * The surcharge a method adds to `amount` — a percentage of that amount only.
 *
 * In split mode `amount` is one row's share, so a card at 3% covering $100 of a
 * $200 check charges $3, never $6: the surcharge follows the portion actually
 * paid with that method.
 */
export function computeSurcharge(method: SurchargeMethod | null | undefined, amount: number): number {
  if (!method?.surcharge_percentage) return 0
  return Number(((amount || 0) * (method.surcharge_percentage / 100)).toFixed(2))
}

/**
 * What the customer hands over for one tender row: the balance it covers plus
 * its own surcharge.
 *
 * The surcharge rides on top of `amount_applied` and is never part of the split
 * balance — the applied amounts alone have to add up to the check, or the
 * surcharge would have to be charged on itself.
 */
export function rowDue(row: TenderAmounts): number {
  return (row.amount_applied || 0) + (row.surcharge || 0)
}

/** Change owed on a cash row. Never negative: short payments are blocked, not refunded. */
export function computeChange(row: TenderAmounts, amountTendered: number): number {
  return Math.max(0, (amountTendered || 0) - rowDue(row))
}
